import { useState } from 'react'
import { X, User, Shield, Building, Phone, Briefcase, Lock } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { userApi } from '@/lib/api/users'
import { useAuth } from '@/contexts/AuthContext'
import toast from 'react-hot-toast'

interface UserEditModalProps {
  user: any
  onClose: () => void
  onSuccess: () => void
}

export function UserEditModal({ user, onClose, onSuccess }: UserEditModalProps) {
  const { user: currentUser } = useAuth()
  const [activeTab, setActiveTab] = useState<'details' | 'password'>('details')
  
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    role: user.role || 'viewer',
    is_active: user.is_active ?? true,
    department: user.department || '',
    job_title: user.job_title || '',
    phone: user.phone || ''
  })
  
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const updateMutation = useMutation({
    mutationFn: () => userApi.updateUser(user.id, formData),
    onSuccess: () => {
      toast.success('User updated successfully')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update user')
    }
  })
  
  const resetPasswordMutation = useMutation({
    mutationFn: () => userApi.resetUserPassword(user.id, passwordData.newPassword),
    onSuccess: () => {
      toast.success('Password reset successfully')
      setPasswordData({ newPassword: '', confirmPassword: '' })
      setActiveTab('details')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reset password')
    }
  })
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    
    if (activeTab === 'details') {
      updateMutation.mutate()
    } else {
      // Validate passwords
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setErrors({ confirmPassword: 'Passwords do not match' })
        return
      }
      
      if (passwordData.newPassword.length < 8) {
        setErrors({ newPassword: 'Password must be at least 8 characters' })
        return
      }
      
      resetPasswordMutation.mutate()
    }
  }
  
  const canEditRole = () => {
    if (!currentUser) return false
    if (currentUser.id === user.id) return false // Can't change own role
    if (currentUser.role === 'super_admin') return true
    if (currentUser.role === 'admin' && user.role === 'viewer') return true
    return false
  }
  
  const formatRole = (role: string) =>
    role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  
  const canResetPassword = () => {
    if (!currentUser) return false
    if (currentUser.id === user.id) return false // Can't reset own password this way
    return currentUser.role === 'admin' || currentUser.role === 'super_admin'
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-[#000742] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-[#000742] border-b border-gray-200 dark:border-[#5869ff]/20 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit User
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'details'
                  ? 'bg-[#5869ff] text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a1f5c]'
              }`}
            >
              User Details
            </button>
            {canResetPassword() && (
              <button
                onClick={() => setActiveTab('password')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'password'
                    ? 'bg-[#5869ff] text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a1f5c]'
                }`}
              >
                Reset Password
              </button>
            )}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 dark:bg-[#1a1f5c]/20 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-white">{user.email}</p>
              </div>
              
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Department
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Job Title
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.job_title}
                        onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Account Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Account Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        disabled={!canEditRole()}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {/* Admins may only assign Viewer; a role they cannot assign is still shown, disabled, so the true role is visible. */}
                        {currentUser?.role !== 'super_admin' && formData.role !== 'viewer' && (
                          <option value={formData.role} disabled>{formatRole(formData.role)}</option>
                        )}
                        <option value="viewer">Viewer</option>
                        {currentUser?.role === 'super_admin' && (
                          <option value="admin">Admin</option>
                        )}
                        {currentUser?.role === 'super_admin' && (
                          <option value="super_admin">Super Admin</option>
                        )}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <div className="flex items-center gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.is_active}
                          onChange={() => setFormData({ ...formData, is_active: true })}
                          disabled={user.id === currentUser?.id}
                          className="text-[#5869ff] focus:ring-[#5869ff]"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!formData.is_active}
                          onChange={() => setFormData({ ...formData, is_active: false })}
                          disabled={user.id === currentUser?.id}
                          className="text-[#5869ff] focus:ring-[#5869ff]"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Inactive</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  You are about to reset the password for <strong>{user.email}</strong>. 
                  The user will need to use this new password to log in.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      placeholder="Min 8 characters"
                    />
                  </div>
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newPassword}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                      placeholder="Confirm password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-[#5869ff]/20">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending || resetPasswordMutation.isPending}
              className="px-4 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {activeTab === 'details'
                ? (updateMutation.isPending ? 'Updating...' : 'Update User')
                : (resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}